import { PdfTemplateEntityType } from '../types/pdf_template';

export namespace Routing {
  export const dashboard = () => '/dashboard';

  export const pdfTemplateEditor = {
    create: (type: PdfTemplateEntityType) => `/pdf_template_editor/create/${type}`,
    update: (id: string) => `/pdf_template_editor/update/${id}`,
  };

  export const stock = {
    products: {
      overview: () => '/stock/products',
      detail: (id: string) => `/stock/products/${id}/info`,
      settings: {
        overview: () => '/stock/products/settings/import_configurations',
        importConfigurations: {
          overview: () => '/stock/products/settings/import_configurations',
          update: (id: string) => `/stock/products/settings/import_configurations/update/${id}`,
        },
        report: {
          overview: () => '/stock/products/settings/report',
          create: () => '/stock/products/settings/report/create',
          update: (id: string) => `/stock/products/settings/report/update/${id}`,
        },
        tableSettings: {
          overview: () => '/stock/products/settings/table_settings',
        },
      },
    },
    bins: {
      overview: () => '/stock/bins',
      detail: (id: string) => `/stock/bins/${id}`,
      settings: {
        overview: () => '/stock/bins/settings',
        binStatuses: () => '/stock/bins/settings/bin_statuses',
        importConfigurations: {
          overview: () => '/stock/bins/settings/import_configurations',
          update: (id: string) => `/stock/bins/settings/import_configurations/update/${id}`,
        },
      },
    },
    lots: {
      overview: () => '/stock/lots',
      detail: (id: string) => `/stock/lots/${id}/info`,
      settings: {
        overview: () => '/stock/lots/settings/import_configurations',
        importConfigurations: {
          overview: () => '/stock/lots/settings/import_configurations',
          update: (id: string) => `/stock/lots/settings/import_configurations/update/${id}`,
        },
        tableSettings: {
          overview: () => '/stock/lots/settings/table_settings',
        },
      },
    },
    containers: {
      overview: () => '/stock/containers',
      create: () => '/stock/containers/create',
      detail: (id: string) => `/stock/containers/${id}/info`,
      settings: {
        containerTypes: () => '/stock/containers/settings/container_types',
        tableSettings: () => '/stock/containers/settings/table_settings',
      },
    },
    history: {
      overview: () => '/stock/history',
      settings: {
        overview: () => '/stock/history/settings/report',
        report: {
          overview: () => '/stock/history/settings/report',
          create: () => '/stock/history/settings/report/create',
          update: (id: string) => `/stock/history/settings/report/update/${id}`,
        },
      },
    },
  } as const;

  export const productMasterData = {
    overview: () => '/reference_data',
    create: () => '/reference_data/create',
    update: (id: string) => {
      return {
        info: () => `/reference_data/update/${id}/info`,
        attributes: () => `/reference_data/update/${id}/attributes`,
        comments: () => `/reference_data/update/${id}/comments`,
        stockTriggers: () => `/reference_data/update/${id}/stock_triggers`,
        files: () => `/reference_data/update/${id}/files`,
      };
    },
    settings: {
      overview: () => '/reference_data/settings/import_configurations',
      importConfigurations: {
        overview: () => '/reference_data/settings/import_configurations',
        update: (id: string) => `/reference_data/settings/import_configurations/update/${id}`,
      },
      stockTriggers: {
        overview: () => '/reference_data/settings/stock_triggers',
        create: () => '/reference_data/settings/stock_triggers/create/general',
        update: (id: string) => `/reference_data/settings/stock_triggers/update/${id}/general`,
        settings: {
          report: {
            overview: () => '/reference_data/settings/stock_triggers/settings/report',
            create: () => '/reference_data/settings/stock_triggers/settings/report/create',
            update: (id: string) => `/reference_data/settings/stock_triggers/settings/report/update/${id}`,
          },
          importConfigurations: {
            overview: () => '/reference_data/settings/stock_triggers/settings/import_configurations',
            update: (id: string) =>
              `/reference_data/settings/stock_triggers/settings/import_configurations/update/${id}`,
          },
        },
      },
      report: {
        overview: () => '/reference_data/settings/report',
        create: () => '/reference_data/settings/report/create',
        update: (id: string) => `/reference_data/settings/report/update/${id}`,
      },
      handlingUnits: {
        overview: () => '/reference_data/settings/handling_units',
      },
      tableSettings: {
        overview: () => '/reference_data/settings/table_settings',
      },
      binConstraints: {
        overview: () => '/reference_data/settings/bin_constraints',
      },
    },
  } as const;

  export const operations = {
    orders: {
      overview: () => '/operations/orders',
      update: (id: string) => {
        return {
          detail: () => `/operations/orders/update/${id}/detail`,
          info: () => `/operations/orders/update/${id}/info`,
          users: () => `/operations/orders/update/${id}/users`,
        };
      },
      settings: {
        overview: () => '/operations/orders/settings/settings',
        report: {
          overview: () => '/operations/orders/settings/report',
          create: () => '/operations/orders/settings/report/create',
          update: (id: string) => `/operations/orders/settings/report/update/${id}`,
        },
        reorderUsers: () => '/operations/orders/settings/reorder_users',
        settings: () => '/operations/orders/settings/settings',
        importConfigurations: {
          overview: () => '/operations/orders/settings/import_configurations',
          update: (id: string) => `/operations/orders/settings/import_configurations/update/${id}`,
        },
        tableSettings: () => '/operations/orders/settings/table',
        pdfTemplates: () => '/operations/orders/settings/pdf_templates',
      },
    },
    contacts: {
      overview: () => '/operations/contacts',
      update: (id: string) => `/operations/contacts/update/${id}`,
    },
  } as const;

  export const tasks = {
    overview: () => '/tasks',
    detail: (taskId: string) => `/tasks/detail/${taskId}/info`,
    settings: {
      overview: () => '/tasks/settings/settings',
      settings: () => '/tasks/settings/settings',
      report: {
        overview: () => '/tasks/settings/report',
        create: () => '/tasks/settings/report/create',
        update: (id: string) => `/tasks/settings/report/update/${id}`,
      },
    },
  };

  export const assets = {
    info: () => '/assets/info',
    pending: () => '/assets/pending',
    history: () => '/assets/history',
    personal: {
      info: () => '/assets/personal/info',
      pending: () => '/assets/personal/pending',
      history: () => '/assets/personal/history',
    },
    detail: (pmdId: string) => {
      return {
        stock: () => `/assets/detail/${pmdId}/stock`,
        history: () => `/assets/detail/${pmdId}/history`,
      };
    },
    settings: {
      overview: () => '/assets/settings/report',
      report: {
        overview: () => '/assets/settings/report',
        create: () => '/assets/settings/report/create',
        update: (id: string) => `/assets/settings/report/update/${id}`,
      },
      tableSettings: {
        overview: () => '/assets/settings/table',
      },
    },
  };

  export const alerts = {
    overview: () => '/alerts',
  };

  export const partner = {
    overview: () => '/partner/overview',
    activity: () => '/partner/activity',
    pending: () => '/partner/pending',
  };

  export const settings = {
    display: {
      details: () => '/settings/company/details',
      customTranslations: () => '/settings/company/custom_translations',
    },
    scanning: {
      barcodeConfigurations: () => '/settings/scanning/barcode_configurations',
      scanRules: () => '/settings/scanning/scan_rules',
    },
    featureToggles: {
      overview: () => '/settings/feature_toggles',
      productTransactionsScanRequiredWhitelistedUsers: () =>
        '/settings/feature_toggles/product_transactions_scan_required_whitelisted_users',
    },
    triggers: () => '/settings/triggers',
    tags: () => '/settings/tags',
    users: {
      overview: () => '/settings/users',
      update: (id: string) => {
        return {
          roles: () => `/settings/users/update/${id}/roles`,
          authentication: () => `/settings/users/update/${id}/authentication`,
          customFields: () => `/settings/users/update/${id}/custom_fields`,
          management: () => `/settings/users/update/${id}/management`,
        };
      },
      advanced: {
        importConfigurations: {
          overview: () => '/settings/users/advanced/import_configurations',
          update: (id: string) => `/settings/users/advanced/import_configurations/update/${id}`,
        },
      },
    },
    stockLocations: {
      overview: () => '/settings/stock_locations/overview',
      map: () => '/settings/stock_locations/map',
      create: () => '/settings/stock_locations/create',
      update: (id: string) => {
        return {
          locationDetails: () => `/settings/stock_locations/update/${id}/location_details`,
          bins: () => `/settings/stock_locations/update/${id}/bins`,
          users: () => `/settings/stock_locations/update/${id}/users`,
          orders: () => `/settings/stock_locations/update/${id}/orders`,
        };
      },
      advanced: {
        importConfigurations: {
          overview: () => '/settings/stock_locations/advanced/import_configurations',
          update: (id: string) => `/settings/stock_locations/advanced/import_configurations/update/${id}`,
        },
      },
    },
    customFields: () => '/settings/custom_fields',
    integrations: () => '/settings/integrations',
    barcodes: {
      overview: () => '/settings/barcodes',
      create: () => '/settings/barcodes/create',
      update: (id: string) => `/settings/barcodes/update/${id}`,
    },
    security: () => '/settings/security',
  };

  export const profile = {
    personalInformation: () => '/profile/personal_information',
  };

  export const admin = {
    company: () => '/admin/company',
    customers: () => '/admin/customers',
    superUsers: () => '/admin/users',
    translations: () => '/admin/translations',
  };

  export const authentication = {
    login: () => '/login',
    signup: () => '/signup',
  };

  export const companies = {
    overview: () => '/company',
  };
}
